import { useEffect, useRef, useState } from "react";
import { api, AUD, AUDc, formatApiErrorDetail } from "../lib/api";
import { Upload, ArrowClockwise, PencilSimple, FilePdf, X, Check, Trash, MagicWand } from "@phosphor-icons/react";
import { toast } from "sonner";
import { isDemoMode } from "../lib/demoStore";

const PRESETS = [
    { k: "westpac", label: "Westpac" },
    { k: "amex", label: "American Express" },
    { k: "macquarie", label: "Macquarie" },
    { k: "cba", label: "Commonwealth Bank" },
    { k: "anz", label: "ANZ" },
    { k: "nab", label: "NAB" },
    { k: "npbs", label: "Newcastle Permanent (NPBS)" },
];

export default function Transactions() {
    const [txns, setTxns] = useState([]);
    const [q, setQ] = useState("");
    const [cat, setCat] = useState("All");
    const [preset, setPreset] = useState("westpac");
    const [selected, setSelected] = useState(new Set());
    const [bulkCat, setBulkCat] = useState("");
    const [bulkAccount, setBulkAccount] = useState("");
    const [editAccountId, setEditAccountId] = useState(null);
    const [editAccountValue, setEditAccountValue] = useState("");
    const [editCategoryId, setEditCategoryId] = useState(null);
    const [knownCategories, setKnownCategories] = useState([]);
    const fileRef = useRef();
    const pdfRef = useRef();
    const [pdfPreview, setPdfPreview] = useState(null);
    const [pdfLoading, setPdfLoading] = useState(false);
    const [pdfSaving, setPdfSaving] = useState(false);

    const load = async () => setTxns((await api.get("/transactions?limit=1000")).data);
    const loadCategories = async () => {
        try {
            const { data } = await api.get("/transactions/categories");
            setKnownCategories(data);
        } catch { /* non-fatal */ }
    };
    useEffect(() => { load(); loadCategories(); }, []);

    // Prompt the user for a new category name, upsert it into
    // lifestyle_categories so it appears everywhere, and return the name.
    const promptNewCategory = async () => {
        const raw = window.prompt("New category name (will appear in Lifestyle too):");
        const name = (raw || "").trim();
        if (!name) return null;
        try {
            await api.post("/lifestyle-categories/ensure", { category: name });
            setKnownCategories(prev => Array.from(new Set([...prev, name])).sort());
            toast.success(`Added "${name}" — now available everywhere`);
            return name;
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Add category failed");
            return null;
        }
    };

    const categories = Array.from(new Set(txns.map(t => t.category))).sort();
    const filtered = txns.filter(t => {
        if (cat !== "All" && t.category !== cat) return false;
        if (q && !`${t.narrative} ${t.account}`.toLowerCase().includes(q.toLowerCase())) return false;
        return true;
    });

    const toggle = (id) => setSelected(s => {
        const n = new Set(s);
        n.has(id) ? n.delete(id) : n.add(id);
        return n;
    });
    const toggleAll = () => {
        if (selected.size === filtered.length) setSelected(new Set());
        else setSelected(new Set(filtered.map(t => t.id)));
    };

    const upload = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        const fd = new FormData();
        fd.append("file", f);
        try {
            const { data } = await api.post(`/transactions/import?preset=${preset}`, fd,
                { headers: { "Content-Type": "multipart/form-data" } });
            const skipMsg = data.skipped ? ` · ${data.skipped} skipped` : "";
            toast.success(`Imported ${data.inserted} transactions (${data.preset})${skipMsg}`);
            await load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Import failed");
        } finally {
            e.target.value = "";
        }
    };

    const recat = async () => {
        const { data } = await api.post("/transactions/recategorise");
        toast.success(`Re-categorised ${data.updated} transactions`);
        await load();
    };

    const wipeImported = async () => {
        if (!window.confirm(
            "Delete ALL imported transactions (CSV + PDF)? Manual entries are kept. This can't be undone."
        )) return;
        try {
            const { data } = await api.delete("/transactions/wipe-imported");
            toast.success(`Wiped ${data.deleted} imported rows — you can re-import cleanly now`);
            setSelected(new Set());
            await load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Wipe failed");
        }
    };

    const deleteByAccount = async () => {
        const raw = window.prompt(
            "Delete every transaction whose ACCOUNT field contains this text " +
            "(case-insensitive). Manual + imported rows are both deleted. " +
            "Example: 'Macquarie' or 'Pendaz'."
        );
        const name = (raw || "").trim();
        if (!name) return;
        if (!window.confirm(`Delete ALL transactions where account contains "${name}"? This can't be undone.`)) return;
        try {
            const { data } = await api.delete(`/transactions/by-account?account=${encodeURIComponent(name)}`);
            toast.success(`Deleted ${data.deleted} transactions matching "${data.account}"`);
            setSelected(new Set());
            await load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
        }
    };

    const dedupTransactions = async () => {
        try {
            const preview = await api.post("/transactions/deduplicate?dry_run=true");
            const p = preview.data;
            if (!p.would_delete) {
                toast.success("No duplicates found — you're clean");
                return;
            }
            if (!window.confirm(
                `Found ${p.duplicate_groups} duplicate groups (${p.would_delete} extra rows to remove). ` +
                `Same date+narrative+amount rows will be de-duped, keeping the earliest. Continue?`
            )) return;
            const { data } = await api.post("/transactions/deduplicate");
            toast.success(`De-duplicated ${data.deleted} rows across ${data.duplicate_groups} groups`);
            setSelected(new Set());
            await load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Dedupe failed");
        }
    };

    // ---- Account Manager (rename an account across every transaction) ----
    const [amOpen, setAmOpen] = useState(false);
    const [amAccounts, setAmAccounts] = useState([]);
    const [amOld, setAmOld] = useState("");
    const [amNew, setAmNew] = useState("");
    const [amBusy, setAmBusy] = useState(false);

    // ---- Category Manager (rename a category everywhere) ----
    const [cmOpen, setCmOpen] = useState(false);
    const [cmOld, setCmOld] = useState("");
    const [cmNew, setCmNew] = useState("");
    const [cmBusy, setCmBusy] = useState(false);

    // ---- Rules Viewer ----
    const [rvOpen, setRvOpen] = useState(false);
    const [rvRules, setRvRules] = useState([]);
    const [rvBusy, setRvBusy] = useState(false);

    const openAccountManager = async () => {
        setAmOpen(true);
        setAmOld(""); setAmNew("");
        try {
            const { data } = await api.get("/transactions/accounts");
            setAmAccounts(data);
        } catch {
            setAmAccounts([]);
        }
    };

    const applyRename = async () => {
        const oldN = amOld.trim(), newN = amNew.trim();
        if (!oldN || !newN) { toast.error("Both fields are required"); return; }
        if (oldN.toLowerCase() === newN.toLowerCase()) { toast.error("New name is the same"); return; }
        if (!window.confirm(`Rename account "${oldN}" → "${newN}" across every matching transaction?`)) return;
        setAmBusy(true);
        try {
            const { data } = await api.post("/transactions/rename-account", { old_name: oldN, new_name: newN });
            toast.success(`Renamed ${data.updated} transactions → "${newN}"`);
            setAmOpen(false);
            await load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Rename failed");
        } finally {
            setAmBusy(false);
        }
    };

    const openCategoryManager = () => { setCmOpen(true); setCmOld(""); setCmNew(""); };
    const applyRenameCategory = async () => {
        const oldN = cmOld.trim(), newN = cmNew.trim();
        if (!oldN || !newN) { toast.error("Both fields are required"); return; }
        if (oldN.toLowerCase() === newN.toLowerCase()) { toast.error("New name is the same"); return; }
        if (!window.confirm(
            `Rename category "${oldN}" → "${newN}" across every transaction, Lifestyle row, and category rule?`
        )) return;
        setCmBusy(true);
        try {
            const { data } = await api.post("/categories/rename",
                { old_name: oldN, new_name: newN });
            toast.success(
                `Renamed → "${newN}" · ${data.transactions} txns · ${data.lifestyle} lifestyle · ${data.rules} rules`
            );
            setCmOpen(false);
            await Promise.all([load(), loadCategories()]);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Rename failed");
        } finally {
            setCmBusy(false);
        }
    };

    const openRulesViewer = async () => {
        setRvOpen(true);
        try {
            const { data } = await api.get("/category-rules");
            setRvRules(data);
        } catch {
            setRvRules([]);
        }
    };
    const deleteRule = async (rid, keyword) => {
        if (!window.confirm(`Delete rule for "${keyword}"? Future imports won't auto-tag it any more.`)) return;
        setRvBusy(true);
        try {
            await api.delete(`/category-rules/${rid}`);
            setRvRules(rules => rules.filter(r => r.id !== rid));
            toast.success("Rule deleted");
        } catch {
            toast.error("Delete failed");
        } finally {
            setRvBusy(false);
        }
    };

    const uploadPdf = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        setPdfLoading(true);
        try {
            const fd = new FormData();
            fd.append("file", f);
            const { data } = await api.post("/transactions/import-preview", fd, {
                headers: { "Content-Type": "multipart/form-data" },
                timeout: 120000,
            });
            if (!data.entries?.length) {
                toast.warning("No transactions extracted from PDF");
            } else {
                setPdfPreview(data);
                toast.success(`Extracted ${data.entries.length} transactions — review before confirming`);
            }
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "PDF import failed");
        } finally {
            setPdfLoading(false);
            if (pdfRef.current) pdfRef.current.value = "";
        }
    };

    const patchPreview = (i, k, v) => setPdfPreview(p => ({
        ...p, entries: p.entries.map((e, idx) => idx === i ? { ...e, [k]: v } : e)
    }));
    const removePreview = (i) => setPdfPreview(p => ({
        ...p, entries: p.entries.filter((_, idx) => idx !== i)
    }));

    const confirmPdfImport = async () => {
        if (!pdfPreview?.entries?.length) return;
        setPdfSaving(true);
        try {
            const { data } = await api.post("/transactions/import-confirm", {
                account: "Imported (PDF)",
                source_file: pdfPreview.source_file || "",
                archive_file_id: pdfPreview.archive_file_id || null,
                entries: pdfPreview.entries.map(e => ({
                    date: e.date, narrative: e.narrative, amount: Number(e.amount),
                    balance: Number(e.balance || 0),
                    category: e.category || "Needs Review",
                    sub_category: e.sub_category || "",
                    treatment: e.treatment || "Review",
                    account: "Imported (PDF)",
                })),
            });
            toast.success(`Imported ${data.inserted} transactions from PDF`);
            setPdfPreview(null);
            await load();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Import failed");
        } finally { setPdfSaving(false); }
    };

    const applyBulk = async () => {
        if ((!bulkCat && !bulkAccount) || selected.size === 0) return;
        const payload = { ids: Array.from(selected) };
        if (bulkCat) payload.category = bulkCat;
        if (bulkAccount) payload.account = bulkAccount;
        const { data } = await api.post("/transactions/bulk", payload);
        const bits = [bulkCat && `category "${bulkCat}"`, bulkAccount && `account "${bulkAccount}"`]
            .filter(Boolean).join(" + ");
        toast.success(`Updated ${data.updated} transactions → ${bits}`);
        setSelected(new Set());
        setBulkCat("");
        setBulkAccount("");
        await load();
    };

    const saveAccountEdit = async (t) => {
        const next = editAccountValue.trim();
        if (!next || next === t.account) { setEditAccountId(null); return; }
        try {
            await api.put(`/transactions/${t.id}`, { ...t, account: next });
            toast.success(`Account updated → "${next}"`);
            setEditAccountId(null);
            await load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
        }
    };

    const saveCategoryEdit = async (t, newCat) => {
        if (!newCat || newCat === t.category) { setEditCategoryId(null); return; }
        try {
            await api.post("/transactions/bulk", { ids: [t.id], category: newCat });
            toast.success(`Category → "${newCat}"`);
            setEditCategoryId(null);
            await load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
        }
    };

    // AI Review — asks Claude to suggest a category for every transaction
    // still flagged Needs Review / treatment=Review.
    const [aiReview, setAiReview] = useState(null); // { groups, total }
    const [aiLoading, setAiLoading] = useState(false);
    const [aiApplying, setAiApplying] = useState(false);
    const needsReviewCount = txns.filter(
        t => t.category === "Needs Review" || t.treatment === "Review"
    ).length;

    const runAiReview = async () => {
        setAiLoading(true);
        try {
            const { data } = await api.post("/transactions/ai-review?limit=200");
            if (!data.total) {
                toast.success("Nothing to review — every transaction is already categorised.");
                return;
            }
            setAiReview({
                ...data,
                groups: data.groups.map(g => ({ ...g, accept: g.suggestion.confidence >= 0.7,
                                                 createRule: g.suggestion.confidence >= 0.85,
                                                 category: g.suggestion.category,
                                                 sub_category: g.suggestion.sub_category,
                                                 treatment: g.suggestion.treatment,
                                                 ruleKeyword: g.key.split(" ").slice(0, 3).join(" ") })),
            });
        } catch (e) {
            toast.error("AI review failed — try again in a moment.");
        } finally { setAiLoading(false); }
    };

    const applyAiReview = async () => {
        if (!aiReview) return;
        const items = aiReview.groups
            .filter(g => g.accept)
            .map(g => ({
                ids: g.ids,
                category: g.category, sub_category: g.sub_category,
                treatment: g.treatment,
                create_rule: g.createRule,
                rule_keyword: g.ruleKeyword,
            }));
        if (!items.length) { toast.error("Tick at least one group to apply."); return; }
        setAiApplying(true);
        try {
            const { data } = await api.post("/transactions/ai-review/apply", { items });
            toast.success(`Applied to ${data.updated} transactions${data.rules_created ? ` · ${data.rules_created} rules saved` : ""}`);
            setAiReview(null);
            await load();
        } catch (e) {
            toast.error("Apply failed");
        } finally { setAiApplying(false); }
    };

    const updateAiRow = (idx, patch) => setAiReview(prev => ({
        ...prev,
        groups: prev.groups.map((g, i) => i === idx ? { ...g, ...patch } : g),
    }));

    return (
        <div>
            <div className="mb-6 flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="label-eyebrow mb-2">Transactions Master</div>
                    <h1 className="font-serif text-4xl lg:text-5xl tracking-tight">Every dollar, categorised.</h1>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    {!isDemoMode() && (
                        <select value={preset} onChange={e => setPreset(e.target.value)}
                                className="border border-[var(--line)] px-3 py-2 rounded text-sm bg-white"
                                data-testid="csv-preset-select">
                            {PRESETS.map(p => <option key={p.k} value={p.k}>{p.label}</option>)}
                        </select>
                    )}
                    <button className="btn-ghost flex items-center gap-2 text-sm" onClick={recat} data-testid="recategorise-btn">
                        <ArrowClockwise size={14} /> Re-run rules
                    </button>
                    <button className="btn-primary flex items-center gap-2 text-sm disabled:opacity-60"
                            onClick={runAiReview} disabled={aiLoading} data-testid="ai-review-btn">
                        <MagicWand size={14} weight="fill" />
                        {aiLoading ? "AI reviewing…"
                                   : needsReviewCount > 0 ? `AI Review (${needsReviewCount})`
                                                          : "AI Review"}
                    </button>
                    {!isDemoMode() && (
                        <>
                            <input ref={fileRef} type="file" accept=".csv" onChange={upload} className="hidden"
                                   data-testid="csv-upload-input" />
                            <button className="btn-ghost flex items-center gap-2 text-sm"
                                    onClick={() => fileRef.current?.click()} data-testid="csv-upload-btn">
                                <Upload size={14} /> Import CSV
                            </button>
                            <input ref={pdfRef} type="file" accept=".pdf" onChange={uploadPdf} className="hidden"
                                   data-testid="pdf-upload-input" />
                            <button className="btn-primary flex items-center gap-2 text-sm disabled:opacity-60"
                                    onClick={() => pdfRef.current?.click()} disabled={pdfLoading}
                                    data-testid="pdf-upload-btn">
                                <FilePdf size={14} /> {pdfLoading ? "Extracting…" : "Import PDF statement"}
                            </button>
                            <button className="text-xs px-3 py-2 rounded border border-transparent hover:border-[var(--line)] flex items-center gap-2"
                                    onClick={dedupTransactions}
                                    data-testid="dedup-btn"
                                    title="Find and remove duplicate rows (same date + narrative + amount)">
                                <ArrowClockwise size={13} /> Deduplicate
                            </button>
                            <button className="text-xs px-3 py-2 rounded border border-transparent hover:border-[var(--line)] flex items-center gap-2"
                                    onClick={openAccountManager}
                                    data-testid="account-manager-btn"
                                    title="Rename an account across every transaction">
                                <PencilSimple size={13} /> Rename account…
                            </button>
                            <button className="text-xs px-3 py-2 rounded border border-transparent hover:border-[var(--line)] flex items-center gap-2"
                                    onClick={openCategoryManager}
                                    data-testid="category-manager-btn"
                                    title="Rename a category across transactions, Lifestyle and rules">
                                <PencilSimple size={13} /> Rename category…
                            </button>
                            <button className="text-xs px-3 py-2 rounded border border-transparent hover:border-[var(--line)] flex items-center gap-2"
                                    onClick={openRulesViewer}
                                    data-testid="rules-viewer-btn"
                                    title="View & delete saved category rules">
                                <MagicWand size={13} /> Rules…
                            </button>
                            <button className="text-xs px-3 py-2 rounded border border-transparent hover:border-[var(--line)] flex items-center gap-2"
                                    style={{ color: "#b91c1c" }}
                                    onClick={deleteByAccount}
                                    data-testid="delete-by-account-btn"
                                    title="Delete all transactions matching an account name (e.g. Macquarie Pendaz)">
                                <Trash size={13} /> Delete by account…
                            </button>
                            <button className="text-xs px-3 py-2 rounded border border-transparent hover:border-[var(--line)] flex items-center gap-2"
                                    style={{ color: "#b91c1c" }}
                                    onClick={wipeImported}
                                    data-testid="wipe-imported-btn"
                                    title="Delete all CSV/PDF imported transactions">
                                <Trash size={13} /> Wipe imported
                            </button>
                        </>
                    )}
                </div>
            </div>

            <div className="flex items-center gap-3 mb-4 flex-wrap">
                <input placeholder="Search narrative or account…"
                       value={q} onChange={e => setQ(e.target.value)}
                       className="flex-1 min-w-[180px] border border-[var(--line)] bg-white px-4 py-2 rounded text-sm"
                       data-testid="txn-search" />
                <select value={cat} onChange={e => setCat(e.target.value)}
                        className="border border-[var(--line)] bg-white px-3 py-2 rounded text-sm max-w-full"
                        data-testid="txn-category-filter">
                    <option value="All">{`All (${txns.length})`}</option>
                    {categories.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
            </div>

            {selected.size > 0 && (
                <div className="hairline bg-[var(--eucalyptus)] text-[var(--bone)] p-4 rounded mb-4 flex items-center gap-3 flex-wrap"
                     data-testid="bulk-bar">
                    <PencilSimple size={16} />
                    <span className="text-sm">{selected.size} selected</span>
                    <select value={bulkCat}
                           onChange={async e => {
                               if (e.target.value === "__NEW__") {
                                   const n = await promptNewCategory();
                                   if (n) setBulkCat(n);
                               } else {
                                   setBulkCat(e.target.value);
                               }
                           }}
                           className="bg-white text-[var(--ink)] border border-white/20 px-3 py-1.5 rounded text-sm w-40"
                           data-testid="bulk-category-input">
                        <option value="">— category —</option>
                        {knownCategories.map(k => (
                            <option key={k} value={k}>{k}</option>
                        ))}
                        <option value="__NEW__">+ Add new category…</option>
                    </select>
                    <input placeholder="New account…" value={bulkAccount}
                           onChange={e => setBulkAccount(e.target.value)}
                           className="bg-white/10 border border-white/20 px-3 py-1.5 rounded text-sm text-[var(--bone)] placeholder-white/60 w-48"
                           list="account-list"
                           data-testid="bulk-account-input" />
                    <datalist id="account-list">
                        {Array.from(new Set(txns.map(t => t.account).filter(Boolean))).map(a => (
                            <option key={a} value={a} />
                        ))}
                    </datalist>
                    <button onClick={applyBulk} disabled={!bulkCat && !bulkAccount}
                            className="bg-white text-[var(--eucalyptus)] px-4 py-1.5 rounded text-sm font-medium disabled:opacity-50"
                            data-testid="bulk-apply-btn">
                        Apply
                    </button>
                    <button onClick={() => { setSelected(new Set()); setBulkCat(""); setBulkAccount(""); }}
                            className="text-white/80 hover:text-white text-sm">Cancel</button>
                </div>
            )}

            <div className="hairline bg-white rounded overflow-hidden">
                <div className="overflow-x-auto">
                <table className="editorial w-full min-w-[720px]" data-testid="transactions-table">
                    <thead>
                    <tr>
                        <th style={{ width: 40 }}>
                            <input type="checkbox" onChange={toggleAll}
                                   checked={filtered.length > 0 && selected.size === filtered.length}
                                   data-testid="select-all-checkbox" />
                        </th>
                        <th>Date</th><th>Account</th><th>Narrative</th><th>Category</th>
                        <th className="num">Debit</th><th className="num">Credit</th><th>Treatment</th>
                    </tr>
                    </thead>
                    <tbody>
                    {filtered.slice(0, 500).map(t => (
                        <tr key={t.id} className={selected.has(t.id) ? "bg-[var(--eucalyptus)]/[0.04]" : ""}>
                            <td><input type="checkbox" checked={selected.has(t.id)} onChange={() => toggle(t.id)}
                                       data-testid={`select-${t.id}`} /></td>
                            <td className="text-[var(--muted)] text-xs whitespace-nowrap">{String(t.date).slice(0, 10)}</td>
                            <td className="text-[var(--muted)] text-xs">
                                {editAccountId === t.id ? (
                                    <input
                                        autoFocus
                                        value={editAccountValue}
                                        list="account-list-inline"
                                        onChange={e => setEditAccountValue(e.target.value)}
                                        onBlur={() => saveAccountEdit(t)}
                                        onKeyDown={e => {
                                            if (e.key === "Enter") saveAccountEdit(t);
                                            if (e.key === "Escape") setEditAccountId(null);
                                        }}
                                        className="border border-[var(--line)] px-2 py-0.5 rounded text-xs w-40"
                                        data-testid={`account-edit-input-${t.id}`}
                                    />
                                ) : (
                                    <button
                                        onClick={() => { setEditAccountId(t.id); setEditAccountValue(t.account || ""); }}
                                        className="hover:underline text-left"
                                        title="Click to edit account"
                                        data-testid={`account-cell-${t.id}`}>
                                        {t.account || "—"}
                                    </button>
                                )}
                            </td>
                            <td className="font-medium max-w-[380px] truncate">{t.narrative}</td>
                            <td>
                                {editCategoryId === t.id ? (
                                    <select
                                        autoFocus
                                        value={t.category || ""}
                                        onChange={async e => {
                                            if (e.target.value === "__NEW__") {
                                                const n = await promptNewCategory();
                                                if (n) await saveCategoryEdit(t, n);
                                                else setEditCategoryId(null);
                                            } else {
                                                await saveCategoryEdit(t, e.target.value);
                                            }
                                        }}
                                        onBlur={() => setEditCategoryId(null)}
                                        data-testid={`category-edit-select-${t.id}`}
                                        className="border border-[var(--line)] bg-white rounded text-xs px-1 py-0.5">
                                        {t.category && !knownCategories.includes(t.category) && (
                                            <option value={t.category}>{t.category}</option>
                                        )}
                                        {knownCategories.map(k => (
                                            <option key={k} value={k}>{k}</option>
                                        ))}
                                        <option value="__NEW__">+ Add new category…</option>
                                    </select>
                                ) : (
                                    <button
                                        onClick={() => setEditCategoryId(t.id)}
                                        title="Click to change category"
                                        data-testid={`category-chip-${t.id}`}>
                                        <span className="chip status-on-plan cursor-pointer hover:opacity-80">
                                            {t.category}
                                        </span>
                                    </button>
                                )}
                            </td>
                            <td className="num font-mono-num text-[var(--terracotta)]">{t.debit ? AUD(t.debit) : "—"}</td>
                            <td className="num font-mono-num text-[var(--eucalyptus)]">{t.credit ? AUD(t.credit) : "—"}</td>
                            <td className="text-[var(--muted)] text-xs">{t.treatment}</td>
                        </tr>
                    ))}
                    </tbody>
                </table>
                <datalist id="account-list-inline">
                    {Array.from(new Set(txns.map(t => t.account).filter(Boolean))).map(a => (
                        <option key={a} value={a} />
                    ))}
                </datalist>
                </div>
                {filtered.length === 0 && (
                    <div className="p-10 text-center text-[var(--muted)]">No transactions match your filters.</div>
                )}
            </div>

            {cmOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
                     onClick={() => !cmBusy && setCmOpen(false)}
                     data-testid="category-manager-modal">
                    <div className="bg-[var(--bone)] hairline rounded max-w-lg w-full"
                         onClick={e => e.stopPropagation()}>
                        <div className="p-6 border-b" style={{ borderColor: "var(--line)" }}>
                            <div className="label-eyebrow mb-1">Category Manager</div>
                            <div className="font-serif text-2xl">Rename a category everywhere</div>
                            <p className="text-xs mt-2" style={{ color: "var(--muted)" }}>
                                Updates every transaction, the matching Lifestyle budget row,
                                and every category rule. Case-insensitive exact match.
                            </p>
                        </div>
                        <div className="p-6 space-y-4 text-sm">
                            <div>
                                <label className="label-eyebrow block mb-1">Current category</label>
                                <select value={cmOld} onChange={e => setCmOld(e.target.value)}
                                        className="border border-[var(--line)] bg-white px-3 py-2 rounded text-sm w-full"
                                        data-testid="category-manager-old-select">
                                    <option value="">— pick a category —</option>
                                    {knownCategories.map(k => (
                                        <option key={k} value={k}>{k}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="label-eyebrow block mb-1">New name</label>
                                <input value={cmNew} onChange={e => setCmNew(e.target.value)}
                                       placeholder="e.g. Groceries"
                                       className="border border-[var(--line)] bg-white px-3 py-2 rounded text-sm w-full"
                                       data-testid="category-manager-new-input" />
                            </div>
                        </div>
                        <div className="p-6 border-t flex justify-between"
                             style={{ borderColor: "var(--line)" }}>
                            <button className="btn-ghost text-xs"
                                    onClick={() => setCmOpen(false)} disabled={cmBusy}
                                    data-testid="category-manager-cancel">Cancel</button>
                            <button className="btn-primary text-sm flex items-center gap-2"
                                    onClick={applyRenameCategory}
                                    disabled={cmBusy || !cmOld || !cmNew}
                                    data-testid="category-manager-apply">
                                {cmBusy ? "Renaming…" : "Rename everywhere"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {rvOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
                     onClick={() => !rvBusy && setRvOpen(false)}
                     data-testid="rules-viewer-modal">
                    <div className="bg-[var(--bone)] hairline rounded max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col"
                         onClick={e => e.stopPropagation()}>
                        <div className="p-6 border-b" style={{ borderColor: "var(--line)" }}>
                            <div className="label-eyebrow mb-1">Category Rules</div>
                            <div className="font-serif text-2xl">Auto-tag rules ({rvRules.length})</div>
                            <p className="text-xs mt-2" style={{ color: "var(--muted)" }}>
                                Each rule maps a keyword found in the narrative to a category.
                                They run on every CSV/PDF import and when you click Re-run rules.
                                Delete a rule to stop auto-tagging those narratives.
                            </p>
                        </div>
                        <div className="overflow-y-auto flex-1">
                            <table className="editorial w-full text-sm">
                                <thead>
                                    <tr><th>Keyword</th><th>Category</th><th>Sub-category</th>
                                        <th>Treatment</th><th style={{ width: 40 }}></th></tr>
                                </thead>
                                <tbody>
                                    {rvRules.length === 0 && (
                                        <tr><td colSpan={5} className="p-6 text-center text-[var(--muted)]">
                                            No rules saved yet. Tick "createRule" in AI Review to save your first one.
                                        </td></tr>
                                    )}
                                    {rvRules.map(r => (
                                        <tr key={r.id} data-testid={`rule-row-${r.id}`}>
                                            <td className="font-mono text-xs">{r.keyword}</td>
                                            <td><span className="chip status-on-plan">{r.category}</span></td>
                                            <td className="text-xs">{r.sub_category || "—"}</td>
                                            <td className="text-xs">{r.treatment}</td>
                                            <td>
                                                <button onClick={() => deleteRule(r.id, r.keyword)}
                                                        disabled={rvBusy}
                                                        className="text-[var(--muted)] hover:text-[var(--terracotta)]"
                                                        data-testid={`rule-delete-${r.id}`}>
                                                    <Trash size={14} />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="p-4 border-t flex justify-end" style={{ borderColor: "var(--line)" }}>
                            <button className="btn-ghost text-xs"
                                    onClick={() => setRvOpen(false)}
                                    data-testid="rules-viewer-close">Close</button>
                        </div>
                    </div>
                </div>
            )}

            {pdfPreview && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
                     style={{ background: "rgba(0,0,0,0.5)" }}
                     data-testid="pdf-preview-modal">
                    <div className="bg-white rounded max-w-6xl w-full max-h-[90vh] overflow-auto shadow-xl">
                        <div className="p-6 border-b flex items-start justify-between"
                             style={{ borderColor: "var(--line)" }}>
                            <div>
                                <div className="label-eyebrow">PDF statement preview</div>
                                <h2 className="font-serif text-2xl mt-1">
                                    {pdfPreview.entries.length} transactions from {pdfPreview.source_file}
                                </h2>
                                <p className="text-xs mt-2" style={{ color: "var(--muted)" }}>
                                    Edit any row before confirming. Delete rows that shouldn&apos;t be imported.
                                    Categories were auto-filled by the existing rule set — override anything
                                    that&apos;s wrong.
                                </p>
                            </div>
                            <button onClick={() => setPdfPreview(null)}
                                    className="text-[var(--muted)] hover:text-[var(--ink)]"
                                    data-testid="pdf-preview-close">
                                <X size={20} />
                            </button>
                        </div>
                        <div className="p-6 overflow-x-auto">
                            <table className="editorial w-full text-sm">
                                <thead>
                                    <tr><th>Date</th><th>Narrative</th>
                                        <th className="num">Amount</th><th>Category</th><th></th></tr>
                                </thead>
                                <tbody>
                                    {pdfPreview.entries.map((e, i) => (
                                        <tr key={i} data-testid={`pdf-preview-row-${i}`}>
                                            <td>
                                                <input type="date" value={e.date || ""}
                                                       onChange={ev => patchPreview(i, "date", ev.target.value)}
                                                       className="border border-[var(--line)] px-2 py-1 rounded text-sm" />
                                            </td>
                                            <td>
                                                <input type="text" value={e.narrative}
                                                       onChange={ev => patchPreview(i, "narrative", ev.target.value)}
                                                       className="border border-[var(--line)] px-2 py-1 rounded text-sm w-full" />
                                            </td>
                                            <td className="num">
                                                <input type="number" step="0.01" value={e.amount}
                                                       onChange={ev => patchPreview(i, "amount", Number(ev.target.value))}
                                                       className="border border-[var(--line)] px-2 py-1 rounded text-sm w-28 text-right font-mono-num"
                                                       style={{ color: e.amount < 0 ? "var(--terracotta)" : "var(--eucalyptus)" }} />
                                                <div className="text-xs text-[var(--muted)] mt-0.5">{AUDc(e.amount)}</div>
                                            </td>
                                            <td>
                                                <input type="text" value={e.category}
                                                       onChange={ev => patchPreview(i, "category", ev.target.value)}
                                                       className="border border-[var(--line)] px-2 py-1 rounded text-sm w-full" />
                                            </td>
                                            <td>
                                                <button onClick={() => removePreview(i)}
                                                        className="text-[var(--muted)] hover:text-[var(--terracotta)]"
                                                        data-testid={`pdf-preview-del-${i}`}>
                                                    <Trash size={14} />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="p-6 border-t flex justify-end gap-2"
                             style={{ borderColor: "var(--line)" }}>
                            <button onClick={() => setPdfPreview(null)} className="btn-ghost text-sm">Cancel</button>
                            <button onClick={confirmPdfImport}
                                    disabled={pdfSaving || pdfPreview.entries.length === 0}
                                    className="btn-primary text-sm inline-flex items-center gap-2 disabled:opacity-60"
                                    data-testid="pdf-preview-confirm">
                                <Check size={14} />
                                {pdfSaving ? "Saving…" : `Confirm ${pdfPreview.entries.length} transactions`}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {aiReview && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
                     data-testid="ai-review-modal">
                    <div className="bg-white rounded max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
                        <div className="p-6 border-b border-[var(--line)] flex items-baseline justify-between flex-wrap gap-2">
                            <div>
                                <div className="label-eyebrow flex items-center gap-2">
                                    <MagicWand size={12} weight="fill" className="text-[var(--eucalyptus)]" /> AI Review
                                </div>
                                <div className="font-serif text-2xl mt-1">
                                    {aiReview.groups.length} narrative groups · {aiReview.total} transactions
                                </div>
                                <div className="text-xs text-[var(--muted)] mt-1">
                                    Tick the groups to accept. Add a rule to auto-classify future imports.
                                    Confidence &lt; 0.7 is unticked by default — sanity-check those.
                                </div>
                            </div>
                            <button onClick={() => setAiReview(null)}
                                    className="text-[var(--muted)] hover:opacity-70"
                                    data-testid="ai-review-close">
                                <X size={20} />
                            </button>
                        </div>
                        <div className="overflow-auto flex-1 p-2">
                            <table className="editorial w-full text-sm">
                                <thead>
                                    <tr>
                                        <th style={{ width: 40 }}></th>
                                        <th>Narrative</th>
                                        <th style={{ width: 100 }}>Count</th>
                                        <th style={{ width: 120 }}>Confidence</th>
                                        <th style={{ width: 130 }}>Category</th>
                                        <th style={{ width: 130 }}>Sub-category</th>
                                        <th style={{ width: 110 }}>Treatment</th>
                                        <th style={{ width: 200 }}>Save as rule</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {aiReview.groups.map((g, i) => {
                                        const conf = g.suggestion.confidence;
                                        const confCls = conf >= 0.85 ? "text-[var(--eucalyptus)]"
                                                       : conf >= 0.6 ? "text-[var(--ink)]"
                                                                     : "text-[var(--terracotta)]";
                                        return (
                                            <tr key={g.key}
                                                data-testid={`ai-review-row-${i}`}
                                                className={g.accept ? "" : "opacity-50"}>
                                                <td>
                                                    <input type="checkbox" checked={g.accept}
                                                           data-testid={`ai-accept-${i}`}
                                                           onChange={e => updateAiRow(i, { accept: e.target.checked })} />
                                                </td>
                                                <td className="text-xs">{g.sample_narrative}</td>
                                                <td className="font-mono-num text-xs">×{g.count}</td>
                                                <td className={`font-mono-num text-xs ${confCls}`}>
                                                    {(conf * 100).toFixed(0)}%
                                                </td>
                                                <td>
                                                    <select value={g.category}
                                                            onChange={async e => {
                                                                if (e.target.value === "__NEW__") {
                                                                    const n = await promptNewCategory();
                                                                    if (n) updateAiRow(i, { category: n });
                                                                } else {
                                                                    updateAiRow(i, { category: e.target.value });
                                                                }
                                                            }}
                                                            data-testid={`ai-review-category-${i}`}
                                                            className="w-full border border-[var(--line)] bg-white rounded text-xs px-1 py-0.5">
                                                        {/* Include the current suggestion even if it's not in the known list yet */}
                                                        {g.category && !knownCategories.includes(g.category) && (
                                                            <option value={g.category}>{g.category}</option>
                                                        )}
                                                        {knownCategories.map(k => (
                                                            <option key={k} value={k}>{k}</option>
                                                        ))}
                                                        <option value="__NEW__">+ Add new category…</option>
                                                    </select>
                                                </td>
                                                <td>
                                                    <input value={g.sub_category}
                                                           onChange={e => updateAiRow(i, { sub_category: e.target.value })}
                                                           className="w-full border-none bg-transparent text-xs" />
                                                </td>
                                                <td>
                                                    <select value={g.treatment}
                                                            onChange={e => updateAiRow(i, { treatment: e.target.value })}
                                                            className="border-none bg-transparent text-xs w-full">
                                                        <option>Include</option>
                                                        <option>Include income</option>
                                                        <option>Exclude</option>
                                                        <option>Review</option>
                                                    </select>
                                                </td>
                                                <td>
                                                    <div className="flex items-center gap-2">
                                                        <input type="checkbox" checked={g.createRule}
                                                               data-testid={`ai-rule-${i}`}
                                                               onChange={e => updateAiRow(i, { createRule: e.target.checked })} />
                                                        <input value={g.ruleKeyword}
                                                               onChange={e => updateAiRow(i, { ruleKeyword: e.target.value })}
                                                               placeholder="keyword"
                                                               className="flex-1 border-b border-[var(--line)] bg-transparent text-xs" />
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                        <div className="p-4 border-t border-[var(--line)] flex items-center justify-between">
                            <div className="text-xs text-[var(--muted)]">
                                {aiReview.groups.filter(g => g.accept).length} of {aiReview.groups.length} ticked
                                {" · "}
                                {aiReview.groups.filter(g => g.accept && g.createRule).length} rules will be saved
                            </div>
                            <button onClick={applyAiReview} disabled={aiApplying}
                                    className="btn-primary text-sm inline-flex items-center gap-2 disabled:opacity-60"
                                    data-testid="ai-review-apply">
                                <Check size={14} />
                                {aiApplying ? "Applying…" : "Apply ticked"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
